import type { PortfolioExperienceItem } from '../../firebase/types/portfolio.types';
import { lines } from './model';
import { Band, SectionIndex } from './ui';

function Detail({ label, value }: { label: string; value?: string }) {
  const text = value?.trim();
  if (!text) return null;
  return (
    <div className="grid gap-1 sm:grid-cols-[9.5rem_1fr] sm:gap-6">
      <dt className="text-sm text-faint">{label}</dt>
      <dd className="text-sm leading-relaxed text-muted md:text-base">{text}</dd>
    </div>
  );
}

function Role({ exp }: { exp: PortfolioExperienceItem }) {
  const points = lines(exp.points);
  return (
    <li className="panel p-4 md:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-xl font-semibold tracking-tight md:text-2xl">{exp.role}</h3>
          {exp.company ? <p className="mt-1 text-sm text-accent">{exp.company}</p> : null}
        </div>
        {(exp.period || exp.location) ? (
          <p className="text-sm text-faint">{[exp.period, exp.location].filter(Boolean).join(' · ')}</p>
        ) : null}
      </div>
      <dl className="mt-4 space-y-3">
        <Detail label="Tech" value={exp.techStack} />
        <Detail label="Systems" value={exp.systemsBuilt} />
        <Detail label="Performance" value={exp.performanceImprovements} />
        <Detail label="Components" value={exp.reusableComponents} />
      </dl>
      {exp.impact?.trim() ? (
        <p className="mt-4 border-l-2 border-signal pl-3 text-sm leading-relaxed text-ink md:text-base">
          {exp.impact.trim()}
        </p>
      ) : null}
      {points.length ? (
        <ul className="mt-4 grid gap-2 text-sm leading-relaxed text-muted md:grid-cols-2">
          {points.map((point, i) => (
            <li key={i} className="flex gap-2">
              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent" aria-hidden="true" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export default function ExperienceSection({
  experience,
  index,
}: {
  experience: PortfolioExperienceItem[];
  index: string;
}) {
  return (
    <Band id="experience">
      <SectionIndex index={index} title="Experience" />
      <ol className="space-y-3">
        {experience.map((exp, i) => (
          <Role key={`${exp.role}-${i}`} exp={exp} />
        ))}
      </ol>
    </Band>
  );
}
